/* eslint-disable import/no-extraneous-dependencies */
import { CheckboxGroup, Tooltip } from 'crux';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import { get, at, isObject, isPlainObject } from 'lodash';
import { updateAnswersArray } from '../redux/actions';
import {
  getWidth,
  getTooltipProps,
  getLabelProps,
  getCSSVariables,
  getTimeStampData,
  getGroupQAriaLabel,
} from './utils';
import TextElement from '../Components/UIElements/TextElement';
import SelectWrapper from './SelectWrapper';
import { parseHtml, scrollQuestionIntoView, getUTCTimestamp } from '../utils';
import { updateQuestionAnswerTimeStamp } from '../redux/actions/questions';

export const TooltipWrapper = styled.div`
  display: flex;
  align-items: center;
  &div: first-child {
    padding: 0.5em;
    padding-left: 0px;
  }
  & div {
    margin-right: 10px;
    margin-bottom: 0px;
  }
`;

const getSelectedOptions = (response) => {
  if (!response) {
    return [];
  }
  if (response && response.length) {
    if (!isObject(response[0])) {
      return response;
    }
    return response.map((r) => (isPlainObject(r) ? r.id : r));
  }
  return [];
};

const getCurrentAnswerById = (answersArray, id) => {
  return answersArray.filter((ans) => ans.question_id === id);
};

const MultiSelect = ({
  question,
  handleChange,
  theme,
  isEditMode,
  activeSubsectionId,
  enableESignCheckbox,
  handlePopup,
  answersArray,
  lastFocusedElementId,
  role,
  updateTimeStamp,
  meta,
  questionsArray,
  errorsArray,
}) => {
  const {
    pageId,
    config: {
      theme: {
        pageOverrides,
        components,
        global: { colorScheme, checkboxTheme },
      },
    },
  } = theme;
  const themeConfig = checkboxTheme && checkboxTheme.themeConfiguration;
  const screenReaderLabelQuestionId = get(question, 'screen_reader_label_id', null);
  const ariaLabel = getGroupQAriaLabel(screenReaderLabelQuestionId, questionsArray, pageId);
  const {
    question_id: questionId,
    question_text: questionText,
    display_type: displayType,
    hint_title: tooltipTitle,
    hint_text: tooltipBody,
    hint_html: tooltipHTML,
    response,
    orientation,
    tooltip,
    is_editable: isEditable,
    transaction_log: transactionLog,
    last_submitted_at: lastSubmittedAt,
    force_check_validation: forceCheckValidation,
  } = question;
  let options = get(question, 'response_options', []);
  const reset = get(question, 'validations.reset_on', null);
  const timeStampRef = useRef(null);
  const hasError = !!errorsArray.find((q) => q?.question_id === questionId);
  const customErrMsg = get(question, 'validations.required.error_message', '');
  const labelProps = getLabelProps('multiSelect', components, pageId, pageOverrides);

  const checkboxEl = document.getElementById(
    `checkbox-option-${questionId}-${lastFocusedElementId}`,
  );

  // Tab Navigation
  useEffect(() => {
    const shouldFocus = options.filter((x) => x.id === lastFocusedElementId);
    if (shouldFocus.length) {
      if (checkboxEl) {
        checkboxEl.focus();
      }
    }
  }, [checkboxEl]);

  useEffect(() => {
    if (isEditMode || Array.isArray(question.response)) {
      question.response.forEach((element) => {
        handleChange(
          {
            id: isPlainObject(element) ? element.id : element,
            checked: true,
            type: 'array',
          },
          true,
          reset,
          isEditMode,
        );
      });
      updateTimeStamp({ transactionLog, lastSubmittedAt }, true);
    }
  }, []);

  const pdfScrollToLast = get(labelProps, 'pdfScrollToLast', false);

  // disable esign checkbox until the user scrolls through all of the PDF pages
  if (pdfScrollToLast && activeSubsectionId === 'signature' && options.length > 0) {
    options[0].disabled = !enableESignCheckbox;
  }

  let selectedOptions = response;
  let isControlledInput = false;

  if (reset) {
    const currentAnswer = getCurrentAnswerById(answersArray, questionId);
    isControlledInput = true;
    if (currentAnswer.length > 0) {
      selectedOptions = currentAnswer[0].answer;
    }
  }
  const disableOnSelect = get(question, 'validations.disable_on_select', []);

  if (selectedOptions.length > 0) {
    const isDisabled = disableOnSelect.some((dOpt) => selectedOptions.includes(dOpt.id));
    options = options.map((opt) => {
      if (disableOnSelect.find((o) => o.id === opt.id)) {
        return { ...opt, disabled: !isDisabled };
      }
      return { ...opt, disabled: isDisabled };
    });
  }

  const tooltipProps = getTooltipProps(tooltipTitle, tooltipBody, tooltipHTML || tooltip);
  const labelTheme = get(labelProps, 'labelText.themeConfiguration', {});
  const element = '.multiSelect';
  const selectDesElement = '.multiSelect-label';
  const cssVariables = getCSSVariables(theme);
  const font = at(cssVariables, [`pageOverride.font${element}`, `global.font${element}`]);
  const selectDesFont = at(cssVariables, [
    `pageOverride.font${selectDesElement}`,
    `global.font${selectDesElement}`,
  ]);
  const isAsteriskConfig = get(theme, 'config.theme.components.inputAsteriskRequired');
  const tooltipColor = get(theme, 'config.theme.global.toolTip.color', '');
  const tooltipFont = get(theme, 'config.theme.global.toolTip.font', '');
  let isAsterisk;
  if (
    isAsteriskConfig &&
    question.validations.required &&
    question.validations.required.value === true
  ) {
    isAsterisk = true;
  }

  const handleFocus = (e) => {
    const el = e.target;
    scrollQuestionIntoView({
      element: el,
      theme,
      role,
    });
    timeStampRef.current = {};
    timeStampRef.current.startTime = getUTCTimestamp();
  };

  const handleBlur = () => {
    updateTimeStamp(getTimeStampData(timeStampRef, transactionLog, meta));
    // updateAnswerArrayInSocket();
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <fieldset>
      <legend style={ { fontSize: 0, margin: 0 } }>
        {`${ariaLabel} ${parseHtml(questionText, handlePopup)}`}
      </legend>
      <SelectWrapper onBlur={ handleBlur } width={ getWidth('multiSelect', pageId, pageOverrides) }>
        <TooltipWrapper>
          <TextElement
            id={ `multiselect-label-${questionId}` }
            styleType='label'
            text={ questionText }
            questionObj={ question }
            sectionIndex={ 99 }
            elementTheme={ labelTheme }
            baseFont={ selectDesFont[0] || selectDesFont[1] }
            isAsterisk={ isAsterisk }
          />
          {tooltipProps.hasTooltip && (
            <Tooltip
              ariaLabel={ `${questionText} info dialog` }
              removeAriaLabelforPopper
              themeConfiguration={ { tooltipFont, tooltipTextColor: tooltipColor } }
              { ...tooltipProps }
            />
          )}
        </TooltipWrapper>
        {displayType === 'checkbox' && (
          <CheckboxGroup
            id={ questionId }
            orientation={ orientation || 'columns' }
            selectedOptions={ getSelectedOptions(selectedOptions) }
            handlePopup={ handlePopup }
            onChange={ (e, option) => {
              const { checked } = e.target;
              handleChange(
                {
                  id: option.id,
                  checked,
                  type: 'array',
                },
                false,
                reset,
                true,
              );
            } }
            onFocus={ handleFocus }
            options={ options }
            disableGroup={ !isEditable }
            color={ colorScheme }
            themeConfiguration={ { ...themeConfig, font: font[0] || font[1] } }
            isControlledInput={ isControlledInput }
            shape={ get(checkboxTheme, 'shape', 'sqaure') }
            forceTriggerValidation={ forceCheckValidation && hasError }
            customErrMsg={ forceCheckValidation && customErrMsg }
          />
        )}
      </SelectWrapper>
    </fieldset>
  );
};

MultiSelect.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  question: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => {
  const activeSubsectionId = get(state, 'questions.activeSubsectionId', '');
  const lastFocusedElementId = get(state, 'questions.lastFocusedElementId', '');
  const sectionIndex = get(props, 'sectionIndex', 0);
  const sections = get(state, 'questions.sections', '');
  const role = get(state, 'questions.role', '');
  const hasESign = get(state, 'questions.eSign.hasESign', false);
  const eSignEnable = get(state, 'questions.eSign.eSignEnable', false);
  const enableESignCheckbox = hasESign && eSignEnable;

  const questionsArray = get(sections[activeSubsectionId], `[${sectionIndex}].questionList`, []);

  let answersArray = [];
  let errorsArray = [];
  if (activeSubsectionId && sections) {
    answersArray = get(sections[activeSubsectionId][sectionIndex], 'answersArray');
    errorsArray = get(sections[activeSubsectionId][sectionIndex], 'errorsArray');
  }
  return {
    activeSubsectionId,
    enableESignCheckbox,
    answersArray,
    lastFocusedElementId,
    role,
    meta: state.meta,
    questionsArray,
    errorsArray,
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const questionId = get(props, 'question.question_id', '');
  return {
    handleChange: (response, preventReflexiveCall, reset, hasEdit = false) => {
      const auditRequired = get(props, 'question.audit_required', false);
      const transactionLog = get(props, 'question.transaction_log', []);
      const { sectionIndex, listParentId } = props;
      dispatch(
        updateAnswersArray({
          response,
          questionId,
          sectionIndex,
          hasEdit,
          preventReflexiveCall,
          propQuestion: props.question,
          reset,
          auditRequired,
          listParentId,
          isNestedQ: props.isNestedQ,
          parentListIndex: props.parentListIndex,
          childListIndex: props.childListIndex,
          listIndex: props.listIndex,
          transactionLog,
        }),
      );
    },
    updateTimeStamp: (timeStampData, isPrefilled = false) => {
      dispatch(
        updateQuestionAnswerTimeStamp(questionId, timeStampData, props.sectionIndex, isPrefilled),
      );
    },
    // updateAnswerArrayInSocket: () => {
    //   const childQuestions = get(props, 'question.child_questions', false);
    //   const reflexiveQuestionFlag = get(props, 'question.reflexive_question_flag', false);
    //   dispatch(updateQuestionAnswerInSocket(questionId, childQuestions || reflexiveQuestionFlag));
    // },
  };
};
export default withTheme(connect(mapStateToProps, mapDispatchToProps)(MultiSelect));
